// Pure HUNT3-derived VO2max norms for women + fitness-age calculator.
// Source: HUNT3 Fitness Study (Nes et al., 2011/2013) — women's VO2max
// (ml/kg/min) by 5-year age band. Used purely for client-side display on
// the postmenopausal Today tab. No DB, no server fn — fully unit-testable.
//
// Structure leaves room for "male" later, but KnowMeno is women-only, so
// we only ship the female column today.

export type Sex = "female" | "male";

type NormAnchor = {
  /** Midpoint age (years) of the original 5-year HUNT3 band. */
  age: number;
  /** Median VO2max (ml/kg/min) for an average woman at this anchor age. */
  female: number;
};

// Each anchor is the MIDPOINT of the original 5-year HUNT3 band, paired
// with that band's women's median VO2max. Anchoring at the midpoint (vs
// the lower bound) removes a systematic ~2.5y young bias and lets us
// linearly interpolate between adjacent anchors for a smooth curve.
const ANCHORS: NormAnchor[] = [
  { age: 22, female: 40.1 }, // 20–24
  { age: 27, female: 38.4 }, // 25–29
  { age: 32, female: 36.7 }, // 30–34
  { age: 37, female: 35.1 }, // 35–39
  { age: 42, female: 33.5 }, // 40–44
  { age: 47, female: 31.9 }, // 45–49
  { age: 52, female: 30.4 }, // 50–54
  { age: 57, female: 28.9 }, // 55–59
  { age: 62, female: 27.4 }, // 60–64
  { age: 67, female: 26.0 }, // 65–69
  { age: 72, female: 24.6 }, // 70–74
  { age: 77, female: 23.2 }, // 75–79
  { age: 82, female: 21.8 }, // 80–84
];

const FITNESS_AGE_MIN = 20;
const FITNESS_AGE_MAX = 85;

function normValue(anchor: NormAnchor, sex: Sex): number {
  return sex === "female" ? anchor.female : anchor.female; // male column TBD
}

export type FitnessAgeResult = {
  fitnessAge: number;
  /** chronological age − fitness age. Positive = fitness age is younger. */
  deltaYears: number;
};

/**
 * Map a VO2max value to a fitness age via linear interpolation across the
 * HUNT3 women's age-vs-median curve. Anchors are at band midpoints; we
 * find the two adjacent anchors the user's VO2max falls between and
 * interpolate the age. Above the highest VO2max / below the lowest, we
 * extrapolate along the nearest segment, then clamp to [20, 85].
 */
export function fitnessAgeFromVO2max(
  vo2max: number | null | undefined,
  ageYears: number | null | undefined,
  sex: Sex = "female",
): FitnessAgeResult | null {
  if (
    typeof vo2max !== "number" ||
    !Number.isFinite(vo2max) ||
    vo2max <= 0 ||
    typeof ageYears !== "number" ||
    !Number.isFinite(ageYears) ||
    ageYears <= 0
  ) {
    return null;
  }

  // Anchors are ordered by ascending age, which means DESCENDING VO2max.
  // Find segment [i, i+1] where anchors[i].female >= vo2max >= anchors[i+1].female.
  let segStart = 0;
  let segEnd = ANCHORS.length - 1;
  if (vo2max >= normValue(ANCHORS[0], sex)) {
    // Above the youngest anchor's median — extrapolate along the first segment.
    segStart = 0;
    segEnd = 1;
  } else if (vo2max <= normValue(ANCHORS[ANCHORS.length - 1], sex)) {
    // Below the oldest anchor's median — extrapolate along the last segment.
    segStart = ANCHORS.length - 2;
    segEnd = ANCHORS.length - 1;
  } else {
    for (let i = 0; i < ANCHORS.length - 1; i++) {
      const hi = normValue(ANCHORS[i], sex);
      const lo = normValue(ANCHORS[i + 1], sex);
      if (vo2max <= hi && vo2max >= lo) {
        segStart = i;
        segEnd = i + 1;
        break;
      }
    }
  }

  const a = ANCHORS[segStart];
  const b = ANCHORS[segEnd];
  const vA = normValue(a, sex);
  const vB = normValue(b, sex);
  const denom = vA - vB; // > 0 by construction
  const t = denom === 0 ? 0 : (vA - vo2max) / denom;
  const rawAge = a.age + t * (b.age - a.age);

  const fitnessAge = Math.max(
    FITNESS_AGE_MIN,
    Math.min(FITNESS_AGE_MAX, Math.round(rawAge)),
  );
  return {
    fitnessAge,
    deltaYears: Math.round(ageYears) - fitnessAge,
  };
}

/**
 * Parse "MM / DD / YYYY" or "YYYY-MM-DD" into chronological age in whole
 * years. Returns null for missing/invalid input.
 */
export function ageFromDob(dob: string | null | undefined, now: Date = new Date()): number | null {
  if (!dob || typeof dob !== "string") return null;
  const trimmed = dob.trim();
  let y: number, m: number, d: number;
  const slash = /^(\d{1,2})\s*\/\s*(\d{1,2})\s*\/\s*(\d{4})$/.exec(trimmed);
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (slash) {
    m = parseInt(slash[1], 10);
    d = parseInt(slash[2], 10);
    y = parseInt(slash[3], 10);
  } else if (iso) {
    y = parseInt(iso[1], 10);
    m = parseInt(iso[2], 10);
    d = parseInt(iso[3], 10);
  } else {
    return null;
  }
  if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) return null;
  let age = now.getFullYear() - y;
  const beforeBday =
    now.getMonth() + 1 < m ||
    (now.getMonth() + 1 === m && now.getDate() < d);
  if (beforeBday) age -= 1;
  if (age < 0 || age > 130) return null;
  return age;
}
